import './news_item_card.scss';

import { useSuspenseQuery } from '@apollo/client';
import React from 'react';
import { PermissionButton } from '../../components/Button/PermissionButton';
import { Card } from '../../components/Card/Card';
import { useConfirmation } from '../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Icon } from '../../components/Icon/Icon';
import { KeyValueTable } from '../../components/KeyValueTable/KeyValueTable';
import { useModalPresenter } from '../../components/Modal/PresentedModal';
import { ShopName } from '../../components/ShopName/ShopName';
import { VideoPlayer } from '../../components/video_player/video_player';
import { graphql } from '../../generated';
import { t } from '../../locales/translate';
import { history } from '../../model/history';
import { deleteNews } from '../../model/news';
import { newsPath } from '../../routes';
import { withSuspendedCard } from '../../util/suspense';
import { UpdateNewsItem } from './update_news_item';

const NEWS_ITEM_CARD = graphql(`
  query NewsItemCardQuery($id: ID!) {
    newsItem(id: $id) {
      id
      createdAt
      shopId
      title
      titleDe
      titleEn
      textDe
      textEn
      category
      url
      inverse
      order
      meta
      asset {
        ... on Image {
          url(width: 200)
          originalUrl
        }
        ... on Video {
          url
        }
        fileType
        id
      }
      permissions {
        id
        destroy
        update
      }
    }
  }
`);

function newsCategoryName(category?: string | null): string | undefined {
  if (!category) {
    return;
  }
  switch (category) {
    case 'regular':
      return t.model.news.regular;
    case 'hot':
      return t.model.news.hotNews;
    case 'feature':
      return t.model.news.featureNews;
  }
  return;
}

interface Props {
  id: string;
}

function NewsItemCard$({ id }: Props): JSX.Element {
  const { data, refetch } = useSuspenseQuery(NEWS_ITEM_CARD, { variables: { id } });
  const modal = useModalPresenter();
  const newsItem = data.newsItem;

  const { confirm } = useConfirmation({
    title: t.model.news.deleteNewsItem.title,
    message: t.model.news.deleteNewsItem.description,
    actionName: t.model.news.deleteNewsItem.confirm,
    action: async () => {
      if (!newsItem) return;
      await deleteNews(newsItem.id);
      history.push(newsPath());
    },
  });

  return (
    <Card
      title={newsItem.title || t.model.news.untitled}
      actions={
        <>
          <PermissionButton
            name="edit"
            model={newsItem}
            permission="update"
            onClick={() => modal.present(UpdateNewsItem, { id, submit: refetch })}
          />
          <PermissionButton name="trash" model={newsItem} permission="destroy" onClick={confirm} />
        </>
      }
    >
      <div className="news-item-card__media">
        {!newsItem.asset && <Icon name="image" className="news-item-card__fallback" />}
        {newsItem?.asset && newsItem.asset.__typename === 'Image' && (
          <a
            className="news-item-card__image"
            href={newsItem.asset.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View Original Image"
          >
            <img src={newsItem.asset.url} />
          </a>
        )}
        {newsItem?.asset && newsItem.asset.__typename === 'Video' && (
          <VideoPlayer type={newsItem.asset.fileType} url={newsItem.asset.url} />
        )}
      </div>
      <div className="news-item-card__details">
        <KeyValueTable
          displayEmpty
          items={[
            {
              key: `${t.general.title} (${t.general.english})`,
              value: newsItem.titleEn,
            },
            {
              key: `${t.general.title} (${t.general.german})`,
              value: newsItem.titleDe,
            },
            {
              key: t.model.shop._name,
              value: <ShopName id={newsItem.shopId || ''} />,
            },
            {
              key: t.general.url,
              value: newsItem.url,
            },
            {
              key: t.model.category._name,
              value: newsCategoryName(newsItem.category),
            },
            {
              key: t.model.news.order,
              value: newsItem.order,
            },
            {
              key: t.model.news.inverse,
              value: newsItem.inverse ? t.general.yes : t.general.no,
            },
            {
              key: `${t.general.text} (${t.general.english})`,
              value: newsItem.textEn,
            },
            {
              key: `${t.general.text} (${t.general.german})`,
              value: newsItem.textDe,
            },
            {
              key: 'title',
              value: t.model.news.meta,
            },
            { key: t.model.news.meta, value: JSON.stringify(newsItem.meta) },
          ]}
        />
      </div>
    </Card>
  );
}

export const NewsItemCard = withSuspendedCard(NewsItemCard$);
